// src/app/services/startup.service.ts
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { catchError, firstValueFrom, of, take, tap } from 'rxjs';
import { SharedService } from './shared.service';
import { UserService } from './user.service';
import { PlanService } from './plan.service';

@Injectable({ providedIn: 'root' })
export class StartupService {
  constructor(
    private msal: MsalService,
    private sharedService: SharedService,
    private userService: UserService,
    private planService: PlanService
  ) {}

  async init(): Promise<void> {
    console.log('[AppInit] Starting MSAL initialization');
    
    try {
      // 1) MSAL init
      await this.msal.instance.initialize();

      // 2) Finish any login/logout redirects and capture account
      const redirectResult: AuthenticationResult | null =
        await this.msal.instance.handleRedirectPromise();

      if (redirectResult?.account) {
        console.log('[AppInit] Redirect result found, setting active account');
        this.msal.instance.setActiveAccount(redirectResult.account);
      }

      // 3) Ensure active account
      let activeAccount = this.msal.instance.getActiveAccount();
      
      if (!activeAccount) {
        const accounts = this.msal.instance.getAllAccounts();
        if (accounts.length) {
          console.log('[AppInit] Setting active account from cache');
          this.msal.instance.setActiveAccount(accounts[0]);
          activeAccount = accounts[0];
        }
      }

      // 4) If still no account, DON'T redirect here - let MsalGuard handle it
      if (!activeAccount) {
        console.log('[AppInit] No active account - skipping data load');
        // Don't call loginRedirect here - it breaks navigation
        // Let Angular routing and MsalGuard handle authentication
        return;
      }

      // 5) Load user data and plans (only if authenticated)
      console.log('[AppInit] Active account found, loading user data');
      
      const user = await firstValueFrom(
        this.userService.refreshUser().pipe(
          take(1),
          tap(user => {
            console.log('[AppInit] User loaded:', user);
            this.sharedService.updateUser(user);
          }),
          catchError(err => {
            console.error('[AppInit] Failed to load user:', err);
            return of(null);
          })
        )
      );

      if (!user) {
        console.log('[AppInit] No user data available');
        return;
      }

      console.log('[AppInit] Loading plans for user:', user.Id);
      
      await firstValueFrom(
        this.planService.getPlans(user.Id).pipe(
          take(1),
          tap(plans => {
            console.log('[AppInit] Plans loaded:', plans);
            
            if (plans?.length) {
              this.sharedService.updatePlan(plans[0]);
            }
            
            if (this.sharedService.updatePlans) {
              this.sharedService.updatePlans(plans);
            }
            
            for (const p of plans ?? []) {
              if (p.FinalReflectionStatus === 'Available') {
                this.sharedService.updatePlanUnderReflection(p.Id);
              }
            }
          }),
          catchError(err => {
            console.error('[AppInit] Failed to load plans:', err);
            return of(null);
          })
        )
      );

      console.log('[AppInit] Initialization complete');
      
    } catch (err) {
      console.error('[AppInit] Critical initialization error:', err);
      // Always allow app to continue
    }
  }
}